import { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAcceptCreatorInvite, useInvite, usePostAuthenticateInvite } from '@/api';
import { useMixpanel } from '@/contexts';
import type { MagicLinkInvite } from '@/types';
import { CREATOR_FUNNEL_EVENTS, trackFunnelEvent } from '@/utils';

export type PostAcceptDestination =
  | { to: '/dashboard' }
  | { to: '/campaign/$id/select-product'; params: { id: number } };

/** New creators pick a product next; returning and already-accepted creators go to the dashboard. */
function getDestination(invite: MagicLinkInvite, alreadyAccepted: boolean): PostAcceptDestination {
  if (alreadyAccepted || invite.hasSignedInBefore) return { to: '/dashboard' };
  return { to: '/campaign/$id/select-product', params: { id: invite.campaignId } };
}

/**
 * The accept step of the invite flow: post-authenticate links the creator to the member,
 * then accept enrols them in the campaign. Both throw on failure so callers decide how to recover.
 */
export function useAcceptInvite(token: string) {
  const { data: invite, refetch: refetchInvite } = useInvite(token);
  const postAuthenticate = usePostAuthenticateInvite();
  const acceptInvite = useAcceptCreatorInvite();
  const { mixpanel } = useMixpanel();
  const [isAccepting, setIsAccepting] = useState(false);

  /** Accepts the invite, records the funnel event and returns where the creator goes next. */
  const acceptAndRoute = async (): Promise<PostAcceptDestination> => {
    const loadedInvite = invite ?? (await refetchInvite({ throwOnError: true })).data;
    if (!loadedInvite) throw new Error('Invite details are unavailable.');

    const { alreadyAccepted } = await acceptInvite.mutateAsync(token);

    if (!alreadyAccepted) {
      trackFunnelEvent(
        mixpanel,
        CREATOR_FUNNEL_EVENTS.INVITE_ACCEPTED,
        {
          campaign_id: String(loadedInvite.campaignId),
          campaign_name: loadedInvite.campaignName,
        },
        [loadedInvite.campaignName]
      );
    }

    return getDestination(loadedInvite, alreadyAccepted);
  };

  /** For a creator who is already signed in: link them with the current session, then accept. */
  const linkAndAccept = async (): Promise<PostAcceptDestination> => {
    setIsAccepting(true);
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      if (!accessToken) throw new Error('No active session to link the creator with.');

      await postAuthenticate.mutateAsync({ magicToken: token, accessToken });
      return await acceptAndRoute();
    } finally {
      setIsAccepting(false);
    }
  };

  return { acceptAndRoute, linkAndAccept, isAccepting };
}
